import type { IconNames } from '@dixa/lib-shared-habla';
import type { ComponentType } from 'react';

import type { FlowNodeTypes } from 'components/powerFlowShared/components/types';

import type { FlowManagerEditModalComponentT } from '../../types';

import type { Relation } from './relation';
import type { ScopeVars } from './scope';

export type NodeAttributes = Record<FlowNodeTypes, GenericNodeAttributes>;

export enum HighlightType {
  ERROR = 'error',
  INFO = 'info',
  NONE = 'none',
}

export type NodePath =
  /** Path highlight from node A to node B */
  | [Relation['id'], Relation['id']]
  /** Path highlight from start to node B */
  | [null, Relation['id']]
  /** Path highlight from node A to end */
  | [Relation['id'] | null]
  /** Nada */
  | [null];

export type DeletableSelection = {
  nodes: Relation['id'][];
  disabled: boolean;
};

export type NodeConnectionTag = {
  icon: IconNames;
  text: string;
};

/*
 * Connection that indicates that node is either connected to a child, or should have a child.
 */
export type NodeConnection = {
  id: Relation['id'];
  connection: Relation['id'];
  label: string;
  labelId?: string;
  tag?: NodeConnectionTag;
};

/*
 * Visual connection exists only in the flow builder.
 * It will not be sent to the backend and it will always end with a tag.
 */
export type NodeVisualConnection = {
  id: Relation['id'];
  connection?: never;
  label: string;
  labelId?: string;
  tag: NodeConnectionTag;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface GenericNodeAttributes<TPayload = any, TExtraData = any> {
  color: number;
  label: string;
  iconName: IconNames;
  helpText?: React.ReactChild;
  helpTextTitle?: React.ReactChild;
  /*
   * Attributes can be set when defining node type attributes.
   * They are used to determine any information about the specific node type instance.
   * e.g is node in a certain channel deprecated or should header text be different.
   */
  attributes?: Record<string, string | number | boolean>;
  translatePayload: (
    node: Relation<TPayload>,
    extraData: TExtraData,
  ) => (NodeConnection | NodeVisualConnection)[];
  translateScopeVariables?: (node: Relation<TPayload>) => ScopeVars;
  updateConnection: (
    node: Relation<TPayload>,
    connection: Relation,
    connectionId: Relation['id'],
  ) => Relation<TPayload>;
  isEditable?: false;
  NodeContent: (props: GenericNodeProps<TPayload, TExtraData>) => JSX.Element;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ActionModal?: ComponentType<FlowManagerEditModalComponentT<any, any> & any>;
  disabled?: boolean;
  badgeContent?: React.ReactChild;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface GenericNodeProps<TPayload = any, TExtraData = any> {
  node: Relation<TPayload>;
  context: { extraData: TExtraData };
  scopeVars: ScopeVars;
}
